<template>
  <div class="notification" :class="messageType">
    <div v-if="messageTitle">
      <component class="w-6 h-6" :is="iconComponent" />
    </div>
    <div>
      <div class="messageTitle" v-if="messageTitle">
        <span class="text-base font-semibold text-dark">{{
          messageTitle
        }}</span>
      </div>
      <span class="content" v-html="message"></span>
      <button
        class="close"
        @click="dismissFn"
        data-test="closeNotificationCard"
      >
        <CloseIcon class="icon" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import CloseIcon from '@/components/ui/svgicons/CloseIcon.vue'
import { MessageTypes } from '@/types'
import InfoIcon from '@/components/ui/svgicons/InfoIcon.vue'
import SuccessIcon from '@/components/ui/svgicons/SuccessIcon.vue'
import WarningIcon from '@/components/ui/svgicons/WarningIcon.vue'

export default defineComponent({
  name: 'NotificationCard',
  components: { CloseIcon },
  props: {
    messageTitle: {
      type: String as PropType<string>,
    },
    messageType: {
      type: String as PropType<MessageTypes>,
      required: true,
      default: MessageTypes.INFO,
    },
    message: {
      type: String as PropType<string>,
      required: true,
    },
    dismissFn: {
      type: Function as PropType<() => any>,
    },
  },
  setup(props) {
    const icons = {
      [MessageTypes.INFO]: InfoIcon,
      [MessageTypes.SUCCESS]: SuccessIcon,
      [MessageTypes.WARNING]: WarningIcon,
      [MessageTypes.ERROR]: InfoIcon,
    }
    const iconComponent = icons[props.messageType] ?? InfoIcon
    return { iconComponent }
  },
})
</script>

<style scoped lang="postcss">
.notification {
  @apply select-none opacity-0 relative
    lg:mb-6 py-4 pl-4 pr-12 border
    border-solid box-border text-sm
    shadow-lg rounded flex flex-row
    flex-nowrap align-top space-x-4
    pointer-events-auto;
  max-width: 400px;
  animation: bounce-in 0.25s ease forwards;
  animation-delay: 1s;
  will-change: transform, opacity;

  .messageTitle {
    @apply flex flex-row items-center justify-start pb-2 pr-2 space-x-2  flex-nowrap;
  }

  .content {
    @apply text-dark;
  }

  button {
    @apply absolute right-0 top-0 cursor-pointer bg-transparent border-none p-0;
    color: inherit !important;
    .icon {
      @apply w-14 h-14 p-4;
      color: inherit;
    }
  }
  &.info {
    @apply bg-primaryLight border-primary text-primary;
  }
  &.success {
    @apply bg-green-50 border-green-500 text-green-500;
  }
  &.warning {
    @apply bg-warningLight border-warning text-warning;
  }
  &.error {
    @apply bg-redLight border-oldRed text-oldRed; 
  }
  &.promo {
    @apply bg-green-50 border-green-300 text-green-500;
    & :deep(a) {
      @apply text-green-500;
    }
  }
  &.announcement {
    @apply bg-warningLight-100 border-warningLight text-warning;
    & :deep(a) {
      @apply text-warning;
    }
  }
  &.action {
    @apply bg-primaryLight border-primary;
    & :deep(a) {
      @apply text-dark underline;
    }
  }
}

@keyframes bounce-in {
  from {
    display: block;
    opacity: 0;
    transform: scale(0);
  }
  to {
    display: block;
    opacity: 1;
    transform: scale(1);
  }
}
</style>
